import styled from 'styled-components';

export const StyledForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-top: 8px;
`;

export const StyledField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  /* Select/RangePicker 가 모달 폭을 채우도록 */
  .ant-select,
  .ant-picker {
    width: 100%;
  }
`;

export const StyledFieldLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
`;

/**
 * 필수 표시 (AMZUWEB-682).
 *
 * 서버가 막는 것만 필수로 찍는다 — 트랙 · 항목명 · 기간.
 * 화면이 서버보다 엄격하면 "왜 이걸 꼭 넣어야 하지" 가 되고,
 * 느슨하면 등록을 누른 뒤에야 실패를 알게 된다. 둘 다 계약이 어긋난 것이다.
 *
 * ⚠️ 색·모양은 어드민 공용 폼(components/form/BaseForm 의 .required, 42곳)을 그대로 따른다 —
 * 보라(#6200ea) 별표다. 이 모달은 BaseForm 이 아니라 자체 레이아웃이라 컴포넌트를 쓸 수 없고,
 * 표기만 승계한다. 빨강으로 두면 같은 어드민 안에서 필수 표시가 두 종류가 된다.
 */
export const StyledRequiredMark = styled.span`
  margin-right: 4px;
  color: #6200ea;
  font-size: 20px;
  font-family: initial;
  line-height: 1;
`;

/** 선택 항목 안내 — 필수가 아닌 것을 굳이 적는 이유는 폼이 길어 어디까지 채워야 할지 가늠이 안 되기 때문 */
export const StyledOptionalMark = styled.span`
  margin-left: 4px;
  font-size: 11px;
  font-weight: 400;
  color: #9ca3af;
`;

/**
 * 관련된 필드를 한 줄로 묶는다.
 *
 * 전부 세로로 쌓으면 8줄이 되어 스크롤이 생기고, 모든 필드가 같은 무게로 보여 무엇이 중요한지 안 읽힌다.
 * `$ratio` 로 열 너비를 준다 — 트랙처럼 값이 짧은 것은 좁게, 항목명처럼 긴 것은 넓게.
 */
export const StyledFieldRow = styled.div<{ $ratio: string }>`
  display: grid;
  grid-template-columns: ${(props) => props.$ratio};
  gap: 12px;
`;

/** 섹션 사이 구분 — 입력 필드와 읽기 전용 정보를 가른다 */
export const StyledDivider = styled.hr`
  margin: 2px 0;
  border: 0;
  border-top: 1px solid #f0f0f0;
`;

/**
 * 연결된 쿠폰 목록.
 *
 * select 의 선택 태그는 숨기고(`StyledCouponSelect`) 연결 상태는 **여기서만** 보여준다 —
 * 태그와 목록이 같은 내용을 두 번 그리면 지저분하고, 태그로는 기간·id 를 담을 수 없다.
 * 역할이 갈린다: select = 검색해서 추가, 목록 = 확인하고 제거.
 *
 * 기간을 노출하는 이유 — 쿠폰이 스케줄 기간과 어긋나는 운영이 흔한데(하루 먼저 여는 등)
 * 서버가 막지 않으므로 사람이 눈으로 견줘봐야 한다.
 */
export const StyledCouponList = styled.ul`
  margin: 6px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 6px;

  > li {
    display: grid;
    grid-template-columns: 60px 1fr auto 24px;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    border: 1px solid #ede9fe;
    border-radius: 6px;
    background: #faf8ff;
    font-size: 12px;
    line-height: 1.5;
  }

  /**
   * 기획전 행은 3열이다 — 쿠폰과 달리 혜택 칸이 없다.
   *
   * 4열 그리드를 그대로 쓰면 이름이 혜택 자리인 60px 칸에 들어가 기획전명이 잘린다.
   */
  > li.is-event {
    grid-template-columns: 1fr auto 24px;
  }

  .benefit {
    font-weight: 700;
    color: #6b21a8;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  .name {
    display: flex;
    align-items: baseline;
    gap: 6px;
    min-width: 0;

    > .title {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .period {
    color: rgba(0, 0, 0, 0.45);
    font-variant-numeric: tabular-nums;
  }

  /* 기간이 스케줄과 어긋나는 쿠폰 — 막지는 않고 눈에만 띄게 한다 */
  .period.is-mismatched {
    color: #c2410c;
    font-weight: 600;
  }

  .remove {
    padding: 0;
    border: 0;
    background: none;
    color: rgba(0, 0, 0, 0.35);
    cursor: pointer;
    line-height: 1;

    &:hover {
      color: rgba(0, 0, 0, 0.65);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.4;
    }
  }
`;

/**
 * 쿠폰 검색 select 자리.
 *
 * 선택 태그는 `tagRender` 로 감춘다 — 연결 상태는 아래 목록이 전담하므로 태그까지 그리면
 * 같은 정보가 두 번 나온다. `.ant-select-selection-item { display: none }` 으로도 되지만
 * antd 내부 클래스라 업그레이드 때 이름이 바뀌면 태그가 조용히 되살아난다.
 */
export const StyledCouponSelect = styled.div``;

export const StyledHint = styled.p`
  margin: 0;
  font-size: 11px;
  color: #9ca3af;
`;

/** 삭제 버튼을 왼쪽 끝, 확인/닫기를 오른쪽에 둔다. */
export const StyledFooter = styled.div`
  display: flex;
  align-items: center;

  .footer-origin {
    display: flex;
    gap: 8px;
    margin-left: auto;
  }

  .delete-button {
    padding: 4px 14px;
    border: 1px solid #fca5a5;
    border-radius: 6px;
    background-color: #fff;
    color: #dc2626;
    font-size: 13px;
    cursor: pointer;

    &:hover:not(:disabled) {
      background-color: #fef2f2;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

/**
 * 모달 제목 줄 — 제목과 "상세 페이지 열기" 버튼을 같은 줄에 둔다 (AMZUWEB-682).
 *
 * 링크를 본문이 아니라 제목 줄에 두는 이유 — 본문은 탭에 따라 바뀌는데 이 링크는
 * 어느 탭에서도 같은 곳을 가리킨다. 제목 옆이 그 성격에 맞는 자리다.
 *
 * ⚠️ 처음엔 밑줄 텍스트 링크였는데 **보라 헤더 위에서 눈에 안 띄었다**(파란 글씨 + 보라 배경).
 * 아이콘 버튼으로 바꾸고 닫기(X) 옆에 세워 "헤더 오른쪽 = 창 단위 동작" 으로 자리를 묶었다.
 *
 * 오른쪽 여백 36px 은 antd 가 X 버튼을 헤더 위에 absolute 로 얹기 때문이다 —
 * 비워두지 않으면 아이콘이 X 밑에 깔린다.
 *
 * 🔴 **이 줄의 높이가 제목 글줄(24px)을 넘으면 안 된다.**
 * antd 는 X 를 헤더 높이의 정중앙(= 콘텐츠 상단에서 28px)에 고정으로 앉히는데,
 * 이 줄은 헤더 안쪽 여백(16px) 아래에서 시작하므로 중앙이 `16 + 줄높이/2` 다.
 * 줄높이가 24 여야 28 로 맞는다 — 28px 짜리 버튼을 넣었더니 30 이 되어 2px 어긋났다.
 */
export const StyledModalTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-right: 36px;
`;

/**
 * 헤더의 아이콘 버튼.
 *
 * 그림만으로는 무슨 동작인지 단정할 수 없어 `title` 로 말을 붙인다 —
 * 아이콘 버튼은 뜻이 분명한 X 같은 것 말고는 항상 설명이 따라와야 한다.
 */
export const StyledModalTitleAction = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* 제목 글줄(24px)을 넘기면 줄이 커져 X 와 높이가 어긋난다 — 위 주석 참고 */
  width: 24px;
  height: 24px;
  border-radius: 4px;
  color: #fff;
  font-size: 16px;

  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.18);
  }
`;
